// ================ router.query との相互作用 ================
// useRoute, useRouter を含むため composable としての利用
import type { LocationQuery, LocationQueryValue } from "vue-router";
import type { Booth, MainViewMode, MapArea, SortOptionsType, QueryFavorite, ComikeMapType, PrintParam, MapSearchCategory, CircleSearchParam } from "~/types/entity/cWebCatalog";
import { useCircleSearchState } from "~/composables/state/useCircleSearchState";
import { useBoothSearchState } from "~/composables/state/useBoothSearchState";
import { getNavigationRoute } from "~/utils/urlHelper";

/**
 * Navigation から呼ばれるページ遷移のためのメソッド
 */
export const changeMainViewMode = async (mode: MainViewMode) => {
  const nextRoute = getNavigationRoute(mode); // 遷移先のページRoute (/catalog など)
  const route = useRoute();
  const mainViewMode = useMainViewModeState();
  const userModel = useUserModel();
  const checkPermission = await userModel.checkPermission(mode);
  // console.log("checkPermission", checkPermission)
  switch (checkPermission) {
    case true: // 権限あり
      // 権限ありでここに到達したということは ログインのためのリダイレクトが不要になったタイミングである
      (useInitialRouteState()).clearInitialRouteFullpath();
      if (nextRoute !== undefined && route.path.toLowerCase() !== nextRoute?.toLowerCase()) {
        await navigateTo(nextRoute);
      }
      mainViewMode.value = mode;
      // mainViewModeToQuery(mode);
      return;
    case false:
      return; // 表示モードは変えない
    default: // undefined = 未ログイン
      await navigateTo(getNavigationRoute("LOGIN"));
      return;
  }
};

/**
 * 単一の文字列を取得する。配列だった場合にはカンマで結合する
 * @param v
 * @returns
 */
const getSingleString = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): string | undefined => {
  if (Array.isArray(v)) {
    return v.join(",");
  }
  if (typeof v === "string") {
    return v;
  }
  return undefined;
};

/**
 * 数値配列に変換する NaN は含めない
 * @param v‘
 * @returns
 */
const getMultiNumber = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): number[] | undefined =>
  getMultiStringCommaSplit(v)?.map(x => Number(x)).filter(x => !isNaN(x));

/**
 * 文字列配列に変換する。単一の値だった場合カンマ区切りで分離した配列とする。ただし値がなかった場合は undefined
 * @param v
 * @returns
 */
const getMultiStringCommaSplit = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): string[] | undefined => {
  if (Array.isArray(v)) {
    return v.map(item => (typeof item === "string" ? item : "")).filter(item => item !== "");
  }
  if (typeof v === "string") {
    return v.split(",");
  }
  return undefined;
};

const getNumber = (v: string | LocationQueryValue | LocationQueryValue[] | undefined): number | undefined => {
  if (Array.isArray(v)) {
    const n = Number(v[0]);
    return isNaN(n) ? undefined : n;
  }
  if (typeof v === "string") {
    const n = Number(v);
    return isNaN(n) ? undefined : n;
  }
  return undefined;
};

/**
 * "Day1" と固定でDay文字列を正規表現を用いて除去して 数値 を取得する
 * @param v "Day1"
 */
const getDayNumber = (v: string | undefined): number | undefined => {
  const match = v?.match(/Day(\d+)/i);
  if (match) {
    const day = Number(match[1]);
    return isNaN(day) ? undefined : day;
  }
  return undefined;
};

export const getQueryFavoriteFromQueryString = (defaultDay: number): QueryFavorite | undefined => {
  const route = useRoute();
  return ({
    tab: getNumber(route.query.tab) ?? 1,
    favorite: getNumber(route.query.favorite),
    day: getNumber(route.query.day) ?? defaultDay,
    sort: getSingleString(route.query.sort)
  });
};

// const mainViewModeToNumber = (mainViewMode: MainViewMode): number => {
//   switch (mainViewMode) {
//     case "FAVORITE":
//       return MainViewModeQueryObject.FAVORITE;
//     case "BOOTH_LIST":
//       return MainViewModeQueryObject.BOOTH_LIST;
//     case "COMIKE_MAP":
//       return MainViewModeQueryObject.COMIKE_MAP;
//     case "COMIKE_PRINT":
//       return MainViewModeQueryObject.COMIKE_PRINT;
//     case "CIRCLE_LIST":
//     default:
//       return MainViewModeQueryObject.CIRCLE_LIST;
//   }
// };

// const mainViewModeFromNumber = (query: MainViewModeQuery | number): MainViewMode => {
//   switch (query) {
//     case MainViewModeQueryObject.TOP_PAGE:
//       return "TOP_PAGE";
//     case MainViewModeQueryObject.BOOTH_LIST:
//       return "BOOTH_LIST";
//     case MainViewModeQueryObject.FAVORITE:
//       return "FAVORITE";
//     case MainViewModeQueryObject.COMIKE_MAP:
//       return "COMIKE_MAP";
//     case MainViewModeQueryObject.COMIKE_PRINT:
//       return "COMIKE_PRINT";
//     case MainViewModeQueryObject.CIRCLE_LIST:
//     case MainViewModeQueryObject.UNKNOWN:
//     default:
//       return "CIRCLE_LIST";
//   }
// };

/**
 * MainViewMode を query の view に設定する
 */
// const mainViewModeToQuery = (mainViewMode: MainViewMode) => {
//   const router = useRouter();
//   router.push({
//     query: {
//       view: mainViewModeToNumber(mainViewMode)
//     }
//   });
// };

/**
 * Query 中の view に設定された値から MainViewMode を取得する
 * @returns
 */
// export const mainViewModeFromQuery = (): MainViewMode => {
//   const route = useRoute();
//   const query = route.query.view ? route.query.view : MainViewModeQueryObject.UNKNOWN;
//   return mainViewModeFromNumber(Number(query));
// };

export const updateQueryCircleList = () => {
  const router = useRouter();
  const param: LocationQuery = getLocationQuery();
  router.push({
    query: {
      // view: MainViewModeQueryObject.CIRCLE_LIST,
      ...param
    }
  });
};
export const updateQueryBoothList = () => {
  const router = useRouter();
  const keyword = useBoothSearchState().get().keyword;
  router.push({
    query: {
      // view: MainViewModeQueryObject.BOOTH_LIST,
      text: keyword
    }
  });
};
export const updateQueryFavorite = () => {
  const router = useRouter();
  const query: QueryFavorite = (useQueryFavoriteState()).get();
  router.push({
    query: {
      // view: MainViewModeQueryObject.FAVORITE,
      ...query
    }
  });
};
export const updateQueryComikeMap = () => {
  const router = useRouter();
  const mapSearch = useMapSearchState().get();
  router.push({
    query: {
      // view: MainViewModeQueryObject.COMIKE_MAP,
      day: mapSearch.day,
      hall: mapSearch.hall,
      wcid: mapSearch.wcid,
      boothId: mapSearch.boothId,
      category: mapSearch.category,
      keyword: mapSearch.keyword,
    }
  });
};

/**
 * hash 変数
 * 先頭の #と/ の間に数値が入っていることがある。これを正規表現で取得し wcid キーとして格納する
 * スラッシュで split し、key=value の形式の文字列を Map として変換する
 * '#20017296/day=Day1/hall=e123/genreCode=500/scale=2'
 */
const convertToMapFromHash = (hash: string | undefined) => {
  if (!hash) return undefined;

  const hashParams = new Map<string, string>();
  const wcidMatch = hash.match(/^#(\d+)?\/(.*)$/);
  if (wcidMatch?.[2]) {
    hashParams.set("wcid", wcidMatch[1]);
    const parts = wcidMatch[2].split("/");
    for (const part of parts) {
      const [key, value] = part.split("=");
      if (key && value) {
        hashParams.set(key, value);
      }
    }
  }
  return hashParams;
};

/**
 * 旧WebCatalogのMapで使われていた引数
 * スラッシュで区切られた値を変換する
 */
const getMapSelectParamFromClassicWebCatalogHash = (hash: string | undefined) => {
  const hashParams = convertToMapFromHash(hash);
  if (hashParams === undefined) {
    return undefined;
  }
  return {
    day: getDayNumber(hashParams.get("day")),
    hall: hashParams.get("hall") as ComikeMapType,
    wcid: getNumber(hashParams.get("wcid")),
    boothId: getNumber(hashParams.get("boothId")),
    category: undefined,
    keyword: getSingleString(hashParams.get("keyword")),
    genreCode: hashParams.get("genreCode"),
  };
};

/**
 * WebCatalogクラシックの詳細検索 (/Search/Result) におけるパラメータ変換
 * https://webcatalog.st.circle.ms/Search/Result?c.Keyword=%E3%82%AD%E3%83%BC%E3%83%AF%E3%83%BC%E3%83%89&c.op=0&c.d1=true&c.d1=false&c.d2=true&c.d2=false&c.cn=true&c.cn=false&c.ck=true&c.ck=false&c.ca=true&c.ca=false&c.cb=true&c.cb=false&c.ct=true&c.ct=false&c.ctw=false&c.cpi=false&c.cu=false&c.cm=false&c.cno=true&c.cno=false&c.clo=false&c.cfm=false&c.cmo=0&c.gls=111&c.gls=112&page=1&c.SortOrderBy=0
 */
const getCircleSearchStateClassic = (query: LocationQuery): CircleSearchParam => {
  // 1日目と2日目の片方が指定された場合のみ、その日を条件として採用する
  // ASP.NET は チェックボックスについて true, false の2つを送信してくる
  const cD1 = query["c.d1"]?.includes ("true");
  const cD2 = query["c.d2"]?.includes ("true");
  const day = (() => {
    switch (true) {
      case cD1 && !cD2:
        return 1;
      case !cD1 && cD2:
        return 2;
      default:
        return undefined;
    }
  })();
  const cKeyword = getSingleString(query["c.Keyword"]);
  const [circleName, keyword] = [undefined, cKeyword];
  // クラシック側のサークル名検索条件はサークル名「も」検索するものである
  // 詳細検索で サークル名 のみに限定して検索したいケースを引き継ぎたいか？は検討の余地あり
  // const [circleName, keyword] = query["c.cn"]?.includes ("true") ? [cKeyword, undefined] : [undefined, cKeyword];

  return ({
    day: day,
    circleName: circleName,
    keyword: keyword,
    favorite: getNumber(query["c.cmo"]) === 1, // お気に入りのみで絞込 1の場合に true
    favoriteMemo: query["c.cfm"]?.includes ("true"),
    genres: getMultiStringCommaSplit(query["c.gls"]),
    canUseElectronicPayment: query["c.clo"]?.includes ("true"), // キャッシュレス決済が使えるサークルで絞り込む
    sort: getNumber(query["c.SortOrderBy"]) as SortOptionsType,
    area: "",
    page: 0, // 検索条件に完全な互換性がないのでpage維持しない
  });

  // 以下は採用しなかったパラメータ
  // query["c.dl"] : 抽選洩れ
  // query["c.cn"] : サークル名
  // c.ck, // ヨミガナ
  // c.ca, // 以下 .* は文字列検索対象として含めるかどうかの判定
  // c.cb,
  // c.cg,
  // c.cu,
  // c.cm,
  // c.ct,
  // c.ctw,
  // c.cpi,
  // c.cno,
  // c.ls, // あいまい検索としてモデルにはあったが実装されていない
  // c.op // AND/OR検索 0の場合 AND 1の場合OR
  // c.range // 検索範囲: (0:全体,1:サークル,2:コスプレイヤー,3:企業,4:お気に入り)
  // c.top // トップ画面のデフォルトの検索条件を採用する
};

const getCircleSearchStateFromQueryString = (query: LocationQuery): CircleSearchParam => ({
  keyword: getSingleString(query.keyword),
  genres: getMultiStringCommaSplit(query.genre),
  area: getSingleString(query.area) ?? "",
  blocks: getMultiStringCommaSplit(query.block),
  day: query.day ? getNumber(query.day) : undefined,
  canUseElectronicPayment: (getNumber(query.pay) ?? 0) > 0,
  sort: getNumber(query.sort) as SortOptionsType,
  page: getNumber(query.page) ?? 1,
  wcid: getNumber(query.wcid),
});

/**
 * CircleSearchStateをクエリパラメータから設定
 */
const setCircleSearchStateFromQuery = (query: LocationQuery, path: string) => {
  const circleSearchState = useCircleSearchState();
  const p = path.toLowerCase() === "/search/result"
    ? getCircleSearchStateClassic(query)
    : getCircleSearchStateFromQueryString(query);
  circleSearchState.set(p);
};

/**
 * BoothSearchStateをクエリパラメータから設定
 */
const setBoothSearchStateFromQuery = (query: LocationQuery) => {
  const boothSearchState = useBoothSearchState();
  boothSearchState.set({
    keyword: getSingleString(query.text),
    page: getNumber(query.page) ?? 1,
  });
};

/**
 * MapSearchStateをクエリパラメータとハッシュから設定
 */
const setMapSearchStateFromQuery = (query: LocationQuery, hash: string) => {
  const mapSearchState = useMapSearchState();
  const stateFromHash = getMapSelectParamFromClassicWebCatalogHash(hash);
  const mapSearchStateDefault = mapSearchState.getDefaultValue();
  // hash よりも query を優先する
  mapSearchState.set({
    day: getNumber(query.day) ?? stateFromHash?.day,
    hall: getSingleString(query.hall) as ComikeMapType ?? stateFromHash?.hall,
    wcid: getNumber(query.wcid) ?? stateFromHash?.wcid,
    boothId: getNumber(query.boothId) ?? stateFromHash?.boothId,
    category: getSingleString(query.category) as MapSearchCategory ?? stateFromHash?.category ?? mapSearchStateDefault.category,
    keyword: getSingleString(query.keyword) ?? stateFromHash?.keyword,
    genreCode: getSingleString(query.genreCode) ?? stateFromHash?.genreCode,
  });
};

/**
 * 各画面用の State 構築 (2025年2月現在 app.vue から呼ばれている)
 */
export const setStateFromQueryString = () => {
  const route = useRoute();
  const query = route.query;
  const path = route.path;
  const hash = route.hash;

  setCircleSearchStateFromQuery(query, path);
  setBoothSearchStateFromQuery(query);
  setMapSearchStateFromQuery(query, hash);
};

/**
 * URL文字列からクエリパラメータを解析してStateを設定
 * ログインのリダイレクトから戻ってきた時に initialRoute を参照した場合に利用する
 */
export const setStateFromUrl = (url: string) => {
  const urlObj = new URL(url, window.location.origin);
  const query = Object.fromEntries(urlObj.searchParams.entries()) as LocationQuery;
  const path = urlObj.pathname;
  const hash = urlObj.hash;

  setCircleSearchStateFromQuery(query, path);
  setBoothSearchStateFromQuery(query);
  setMapSearchStateFromQuery(query, hash);
};

/**
 * QueryString として設定するためのオブジェクトとして取得する 上記 setStateFromQueryString とペア
 */
const getLocationQuery = (): LocationQuery => {
  const circleSearchState = useCircleSearchState();
  const state = circleSearchState.get();
  const q: LocationQuery = {};
  if (state?.keyword) {
    q["keyword"] = state?.keyword;
  }
  if (state?.genres) {
    q["genre"] = state?.genres;
  }
  if (state?.day !== undefined) {
    q["day"] = String(state.day);
  }
  if (state?.area) {
    q["area"] = state?.area;
  }
  if (state?.blocks) {
    q["block"] = state?.blocks;
  }
  if (state.page !== undefined) {
    q["page"] = String(state.page);
  }
  if (state?.prev) {
    q["prev"] = state?.prev;
  }
  if (state?.wcid !== undefined) {
    q["wcid"] = String(state.wcid);
  }
  if (state?.canUseElectronicPayment) {
    q["pay"] = "1";
  }
  if (state?.sort !== undefined) {
    q["sort"] = String(state.sort);
  }
  return q;
};

export const getPrintParam = (): PrintParam => {
  const route = useRoute();
  const printType = getNumber(route.params.printType); // printType という名称は type-[printType].vue に依存しているため params

  // パラメータの取得
  // パラメータ 名称は以下のリクエストクラスに合わせる
  // https://dev.azure.com/cosat/Circlems/_git/WebcatalogPublic?path=%2FSrc%2FComiketWebCatalog%2FCatalog.Web%2FControllers%2FPrintController.cs&version=GCffd83e16e35eda01003e120caee5ecd584cd41a3&line=101&lineEnd=131&lineStartColumn=1&lineEndColumn=15
  // 複数の値の区切り文字はURLエンコードを回避するため アンダースコア とする
  // URL QueryString 全パラメータ埋め込み例
  // /print-type-6?days=1,2&areas=e123,e456,e7,w12,c1&colors=1,2,3,4,5,6,7,8,9&prnColor=1&prnGenre=1&prnMemo=1
  const getDays = (): number[] => getMultiNumber(route.query.days) ?? [];
  const getAreas = (): MapArea[] => getMultiStringCommaSplit(route.query.areas) as MapArea[] ?? [];
  const getColors = (): number[] => getMultiNumber(route.query.colors) ?? [];
  const prnColor = (): boolean => route.query.prnColor === "1";
  const prnGenre = (): boolean => route.query.prnGenre === "1";
  const prnMemo = (): boolean => route.query.prnMemo === "1";

  return {
    printType: printType ?? 0,
    days: getDays(),
    areas: getAreas(),
    colors: getColors(),
    prnColor: prnColor(),
    prnGenre: prnGenre(),
    prnMemo: prnMemo(),
  };
};

/**
 * URLエンコードされたQueryパラメータを生成する
 * @param p
 * @returns "days=1,2&areas=e123,e456,e7,w12,c1&colors=1,2,3,4,5,6,7,8,9&prnColor=1&prnGenre=1&prnMemo=1"
 */
export const getPrintQuery = (p: PrintParam): string => {
  const query = new URLSearchParams();

  // query.append("printType", String(p.printType))
  query.append("days", p.days.join(","));
  query.append("areas", p.areas.join(","));
  query.append("colors", p.colors.join(","));
  query.append("prnColor", p.prnColor ? "1" : "0");
  query.append("prnGenre", p.prnGenre ? "1" : "0");
  query.append("prnMemo", p.prnMemo ? "1" : "0");

  return query.toString();
};

// TODO: URLベースで行うのではなく State + navigateTo で実装する
export const getCircleMapUrl = (circle: { wcid?: number, day?: number | null | undefined, area?: string | null | undefined }) => {
  const mainRoute = getNavigationRoute("COMIKE_MAP");
  return `${mainRoute}?day=${circle.day}&hall=${circle.area}&wcid=${circle.wcid}`;
  // return `${mainRoute}?view=${MainViewModeQueryObject.COMIKE_MAP}&day=${circle.day}&hall=${circle.area}&wcid=${circle.wcid}`;
};

// TODO: URLベースで行うのではなく State + navigateTo で実装する
export const getBoothMapUrl = (booth: Booth) => {
  const mainRoute = getNavigationRoute("COMIKE_MAP");
  return `${mainRoute}?boothId=${booth.boothId}&hall=${booth.area}`;
  // return `${mainRoute}?view=${MainViewModeQueryObject.COMIKE_MAP}&boothId=${booth.boothId}&hall=${booth.area}`;
};
